import { Table as TableAntd } from 'antd'
import cl from 'classnames'
import styles from '@pages/RequirementLaborResources/RequirementLaborResources.module.scss'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getCurrentLanguage } from '@/i18next'
import { LANGUAGE_ENUM as LANG } from '@/shared'
import { toValue } from '@pages/RequirementLaborResources'
import { AntdTable } from '@/components/AntdTable/AntdTable'

export const AgroProducerTable = memo(() => {
	const { t } = useTranslation()
	const lang = getCurrentLanguage()

	const { dataState } = useSelector((state) => ({
		dataState: state.requirementLaborResources.data,
	}))

	const columnLandRegistry = [
		{
			key: 'kdrNum',
			dataIndex: 'kdrNum',
			title: t('requirement_labor_resources.cadastral_number'),
			width: '50%',
		},
		{
			key: 'square',
			dataIndex: 'square',
			title: t('requirement_labor_resources.area_ha'),
			width: '50%',
		},
	]

	const columnSownAreas = [
		{
			key: 'cropName',
			dataIndex: 'cropName',
			title: t('requirement_labor_resources.crop'),
			width: '33%',
			render: (text, record, index) => {
				let label = {
					[LANG.kz]: record.cropKz,
					[LANG.rus]: record.cropRu,
				}
				return label[lang]
			},
		},
		{
			key: 'squareTotal',
			dataIndex: 'squareTotal',
			title: t('requirement_labor_resources.area_ha'),
			width: '33%',
			render: (text, record, index) => {
				return toValue(text)
			},
		},
		{
			key: 'maxTotalHeadCount',
			dataIndex: 'maxTotalHeadCount',
			title: t('requirement_labor_resources.norm_count_season'),
			width: '33%',
			render: (text, record, index) => {
				return toValue(text)
			},
		},
	]

	const columnlivestockCount = [
		{
			key: 'livestockName',
			dataIndex: 'livestockName',
			title: t('requirement_labor_resources.animals'),
			width: '33%',
			render: (text, record, index) => {
				let label = {
					[LANG.kz]: record.kindNameKz,
					[LANG.rus]: record.kindNameRu,
				}
				return label[lang]
			},
		},
		{
			key: 'count',
			dataIndex: 'count',
			title: t('requirement_labor_resources.headcount'),
			width: '33%',
			render: (text, record, index) => {
				return toValue(text)
			},
		},
		{
			key: 'maxHeadCount',
			dataIndex: 'maxHeadCount',
			title: t('requirement_labor_resources.norm_count_season'),
			width: '33%',
			render: (text, record, index) => {
				return toValue(text)
			},
		},
	]

	const renderArray = [
		...(dataState?.data?.agroProducerDto?.landRegistryDtos?.length
			? [
					{
						title: t('requirement_labor_resources.land_fund'),
						render: (
							<AntdTable
								columns={columnLandRegistry}
								dataSource={dataState?.data?.agroProducerDto?.landRegistryDtos}
								pagination={false}
								scroll={{ y: 500 }}
								rowKey={(record) => `${record.kdrNum}-${record.square}`}
								summary={(pageData) => {
									return dataState?.data?.agroProducerDto?.totalSquare ? (
										<TableAntd.Summary.Row>
											<TableAntd.Summary.Cell index={0}>
												{t('requirement_labor_resources.total')}
											</TableAntd.Summary.Cell>
											<TableAntd.Summary.Cell index={1}>
												{toValue(dataState?.data?.agroProducerDto?.totalSquare)}
											</TableAntd.Summary.Cell>
										</TableAntd.Summary.Row>
									) : null
								}}
							/>
						),
					},
			  ]
			: []),
		...(dataState?.data?.agroProducerDto?.sownAreasDto?.length
			? [
					{
						title: t('requirement_labor_resources.sowing_area'),
						render: (
							<AntdTable
								columns={columnSownAreas}
								dataSource={dataState?.data?.agroProducerDto?.sownAreasDto}
								pagination={false}
								scroll={{ y: 500 }}
								rowKey={(record) => `${record.cropName}-${record.squareTotal}`}
								summary={(pageData) => {
									return dataState?.data?.agroProducerDto?.totalSownAreas ||
										dataState?.data?.agroProducerDto?.totalMaxSownAreaHeadCount ? (
										<TableAntd.Summary.Row>
											<TableAntd.Summary.Cell index={0}>
												{t('requirement_labor_resources.total')}
											</TableAntd.Summary.Cell>
											<TableAntd.Summary.Cell index={1}>
												{toValue(dataState?.data?.agroProducerDto?.totalSownAreas)}
											</TableAntd.Summary.Cell>
											<TableAntd.Summary.Cell index={1}>
												{toValue(dataState?.data?.agroProducerDto?.totalMaxSownAreaHeadCount)}
											</TableAntd.Summary.Cell>
										</TableAntd.Summary.Row>
									) : null
								}}
							/>
						),
					},
			  ]
			: []),
		...(dataState?.data?.agroProducerDto?.livestockCountDtos?.length
			? [
					{
						title: t('requirement_labor_resources.livestock_headcount'),
						render: (
							<AntdTable
								columns={columnlivestockCount}
								dataSource={dataState?.data?.agroProducerDto?.livestockCountDtos}
								pagination={false}
								scroll={{ y: 500 }}
								rowKey={(record) => `${record.kindNameRu}-${record.count}`}
								summary={(pageData) => {
									return dataState?.data?.agroProducerDto?.totalLivestock ||
										dataState?.data?.agroProducerDto?.totalMaxLivestockHeadCount ? (
										<TableAntd.Summary.Row>
											<TableAntd.Summary.Cell index={0}>
												{t('requirement_labor_resources.total')}
											</TableAntd.Summary.Cell>
											<TableAntd.Summary.Cell index={1}>
												{toValue(dataState?.data?.agroProducerDto?.totalLivestock)}
											</TableAntd.Summary.Cell>
											<TableAntd.Summary.Cell index={1}>
												{toValue(dataState?.data?.agroProducerDto?.totalMaxLivestockHeadCount)}
											</TableAntd.Summary.Cell>
										</TableAntd.Summary.Row>
									) : null
								}}
							/>
						),
					},
			  ]
			: []),
	]

	return (
		<div className={cl('flex', 'flex-column', 'gap-3', 'pb-4')}>
			{renderArray.map((i, idx) => (
				<div key={`renderArray-${i.title}-${idx}`} className={cl('flex', 'flex-column', 'gap-3', styles.agroTable)}>
					<h4 className={cl('mb-0')}>{i.title}</h4>
					{i.render}
				</div>
			))}
		</div>
	)
})
